/**
 * Strings both case surfaces share.
 *
 * **The state wording is this surface's, not the vocabulary's.** The four values
 * come from the service; how they read belongs to whoever is looking. This queue
 * holds five types with no single kind of counterpart, so it names the generic
 * party — «بانتظار المستخدم» — where the report surfaces name the reporter and
 * the subscriber host names the reader themselves. One value, three readings.
 */
export const sharedCopy = {
  crumbs: {
    console: 'لوحة التشغيل',
    cases: 'الحالات',
  },

  action: {
    clear: 'امسح',
    cancel: 'ألغِ',
  },

  /**
   * `CASE_STATES` as this surface reads them. The set is the service's — a value
   * with no entry here renders as its own token beside a neutral badge, because
   * the badge taxonomy is closed while the vocabulary is not.
   */
  caseStates: {
    open: 'مفتوحة',
    'awaiting user': 'بانتظار المستخدم',
    resolved: 'تمّت المعالجة',
    closed: 'مغلقة',
  } as Readonly<Record<string, string>>,

  /** The five code-owned types. A sixth renders as its own token. */
  caseTypes: {
    SUPPORT_TICKET: 'تذكرة دعم',
    CONTENT_REPORT: 'بلاغ محتوى',
    USER_REPORT: 'بلاغ حساب',
    CONTENT_REQUEST: 'طلب عنوان',
    DEFECT_REPORT: 'بلاغ عطل',
  } as Readonly<Record<string, string>>,

  /** «عن «حافّة النهر»» — the subject, on the two types that carry one. */
  about: 'عن',

  /** The party a message came from, where the service resolved no name. */
  authorSide: {
    RAISER: 'صاحب الحالة',
    OPERATOR: 'فريق الدعم',
  } as Readonly<Record<string, string>>,

  unnamed: 'بلا سبب مسجّل',
  ownerUnknown: 'صاحب غير معروف',
} as const;

/** This surface's word for a state, or the configured token itself. */
export function caseStateLabel(state: string): string {
  return sharedCopy.caseStates[state] ?? state;
}

/** This surface's word for a type, or the token itself. */
export function caseTypeLabel(type: string): string {
  return sharedCopy.caseTypes[type] ?? type;
}

/**
 * **The one state that refuses a reply.**
 *
 * The catalog's `case_states` row settles what the UI/UX pack left open and what
 * the repo's own §23 records as a question: «`resolved` accepts a reply and
 * `closed` does not… a subscriber replying to a resolved case returns it to
 * `open` and the thread continues under the same identifier, while a closed case
 * is terminal and a follow-up is a new case that may quote the old identifier.
 * Both composers are therefore drawn **conditionally on the state** rather than
 * always, and neither is drawn disabled.»
 *
 * The owning service enforces the same rule with `409 SUPPORT_CASE_CLOSED`, so
 * the surface and the wire agree rather than one of them guarding alone.
 */
export const CLOSED_STATE = 'closed';

export function acceptsReply(state: string): boolean {
  return state !== CLOSED_STATE;
}
